import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { NotFoundPage } from './NotFoundPage';
import { getCity } from '../data/cityData';
import { BUSINESS_ID, cityPath } from '../utils/seo';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/full-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Hybrid Ceramic Spray',
  to: '/services/hybrid-ceramic-spray'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  // An unknown city slug used to silently redirect to the homepage, which
  // search engines read as a soft 404 (a real page answering an invalid URL).
  // Serve the noindexed 404 instead.
  if (!city) {
    return <NotFoundPage data-id="element-609" data-mp="1wyf6y70mv2wq" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = cityPath(city.slug);
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront anywhere.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Service Areas',
    item: `${baseUrl}/#service-areas`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} data-id="element-610" data-mp="1su7qgcu7gzb6">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-611" data-mp="22o6oazn8xwxb" data-mpi={i}>{p}</p>)}

      <h2 data-id="element-612" data-mp="1xn98gtppleyz">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-613" data-mp="1xniz8jikvq5q">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-614" data-mp="150oh3t98h2vf">
        <li className="flex items-start" data-id="element-615" data-mp="1iz5yl98jpj4w">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-616" data-mp="1ivry66jfzk03" />
          <span data-id="element-617" data-mp="1euyb1fdtq989">
            <strong data-id="element-618" data-mp="1eym46dfcwtmg">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-619" data-mp="2qb50vm88wkpy">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-620" data-mp="2xv7di6gi4qse" />
          <span data-id="element-621" data-mp="2xxaajag2nk6m">
            <strong data-id="element-622" data-mp="2xxjykshk3h9o">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-623" data-mp="2nyku7m3tq7sh">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-624" data-mp="2svuobf25r7mn" />
          <span data-id="element-625" data-mp="2sy753ow6jby4">
            <strong data-id="element-626" data-mp="3gloytpz08k3q">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-627" data-mp="3lk08o0pauuly">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-628" data-mp="36w5juveexae5" />
          <span data-id="element-629" data-mp="36u1f6b8ow7ct">
            <strong data-id="element-630" data-mp="3buysa9eueb7g">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-631" data-mp="3dc54f9wefie">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-632" data-mp="86ng8slqtig4">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-633" data-mp="21qqnl966opjw">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-634" data-mp="2zdpibedbfnf5">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-635" data-mp="2ugp8csdci0ir" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-636" data-mp="2pl9wu72t8gpi">Explore Our Detailing Services</h2>
      <p data-id="element-637" data-mp="2kpbdwz093qwh">
        Learn more about each of our professional mobile detailing services
        available in {city.name} and throughout Ventura County, or head back to
        our <Link to="/" data-id="element-638" data-mp="3dd75jpayqsme">Ventura County mobile detailing homepage</Link> for
        full pricing and packages:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-639" data-mp="3lmcxy6u3pnfd">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-640" data-mp="3pmohthen3jps">
            {s.label} &rarr;
          </Link>)}
      </div>
    </ServicePageLayout>;
}